import {
  Home,
  Lightbulb,
  Users,
  HeartHandshake,
  Send,
  ArrowDown,
  CheckCircle2,
  Code2,
  Briefcase,
  BookOpen,
  GraduationCap,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { Reveal } from "@/components/Reveal";
import { ROUTE_CONTACT, ROUTE_DEMO } from "@/lib/brand";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "warum", label: "Warum es uns gibt", icon: Lightbulb },
  { id: "team", label: "Das Team", icon: Users },
  { id: "ansatz", label: "Unser Ansatz", icon: HeartHandshake },
  { id: "cta", label: "Kontakt", icon: Send },
];

const WHY = [
  {
    tint: "orange",
    text: "Vereine brauchen bessere Wege, Sponsoring professionell sichtbar zu machen.",
  },
  {
    tint: "blue",
    text: "Unternehmen brauchen mehr Transparenz, Vergleichbarkeit und messbare Wirkung.",
  },
  {
    tint: "orange",
    text: "MatchGate verbindet beide Seiten in einer Plattform, die einfacher erklärt, sucht und auswertet.",
  },
];

// Die Fotos fehlen noch - bis dahin steht ein Monogramm an ihrer Stelle.
const TEAM = [
  {
    initial: "A",
    name: "Alexander Reitzig",
    icon: Code2,
    role: "Produkt & Entwicklung",
    degree: "Bachelor in angewandter Informatik",
    tint: "orange",
    text: "Alexander bringt die technische Perspektive ein: Produktlogik, Plattformstruktur, Datenflüsse und die Frage, wie MatchGate langfristig stabil und intuitiv nutzbar wird.",
  },
  {
    initial: "L",
    name: "Leon Stöhr",
    icon: Briefcase,
    role: "Strategie & Sponsoring",
    degree: "Master in Wirtschaftswissenschaften",
    tint: "blue",
    text: "Leon denkt MatchGate aus wirtschaftlicher Perspektive: Nutzenversprechen, Sponsoringlogik, Marktverständnis und die Frage, wie Vereine und Unternehmen fair zusammenfinden.",
  },
];

const APPROACH = [
  {
    icon: BookOpen,
    title: "Technisch sauber",
    text: "Die Plattform soll nicht nur gut aussehen, sondern klare Abläufe schaffen: vom Profil über das Matching bis zur Auswertung jedes Sponsorings.",
  },
  {
    icon: HeartHandshake,
    title: "Nah am Sponsoring",
    text: "MatchGate entsteht aus dem Wunsch, lokale Partnerschaften weniger zufällig und dafür verständlicher, vergleichbarer und messbarer zu machen.",
  },
  {
    icon: GraduationCap,
    title: "Zwei Blickwinkel",
    text: "Angewandte Informatik und Wirtschaftswissenschaften treffen auf eine gemeinsame Frage: Wie wird Sponsoring für beide Seiten einfacher?",
  },
];

const GRID_PATTERN = {
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
  backgroundSize: "28px 28px",
};

/** Steht anstelle eines Portraits, bis echte Bilder vorliegen. */
const PhotoPlaceholder = ({ initial, tint }) => (
  <div className="relative aspect-[3/4] overflow-hidden rounded-2xl border border-white/10">
    <div className="absolute inset-0 opacity-[0.07]" style={GRID_PATTERN} />
    <div
      className={`absolute -top-12 -left-12 w-52 h-52 rounded-full blur-[70px] ${
        tint === "orange" ? "bg-brand-orange/30" : "bg-brand-blue/30"
      }`}
    />
    <div
      className={`absolute -bottom-12 -right-12 w-52 h-52 rounded-full blur-[70px] ${
        tint === "orange" ? "bg-brand-blue/25" : "bg-brand-orange/25"
      }`}
    />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
      <span className="flex items-center justify-center w-24 h-24 rounded-full border border-white/12 font-display font-black text-4xl text-white/70">
        {initial}
      </span>
      <span className="text-[10px] uppercase tracking-[0.25em] text-white/35">
        Foto folgt
      </span>
    </div>
  </div>
);

const WhyCard = () => (
  <div className="relative">
    <div className="rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/40">
            Warum MatchGate
          </span>
          <h2 className="mt-3 font-display font-bold text-2xl md:text-3xl text-white">
            Sponsoring neu sortieren
          </h2>
        </div>
        <span className="shrink-0">
          <HeartHandshake className="w-6 h-6 text-brand-orange" />
        </span>
      </div>

      <ul className="mt-7 space-y-3">
        {WHY.map((item) => (
          <li
            key={item.text}
            className="flex items-start gap-3.5 rounded-xl border border-white/10 px-4 py-3.5"
          >
            <CheckCircle2
              className={`mt-0.5 w-[18px] h-[18px] shrink-0 ${
                item.tint === "orange" ? "text-brand-orange" : "text-brand-blue"
              }`}
            />
            <span className="text-sm leading-relaxed text-white/70">
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default function About() {
  return (
    <PageLayout sidebarItems={SIDEBAR_ITEMS} title="Über uns" testId="about-main">
      <PageHero
        titleFit={10.4}
        fullHeight
        eyebrow="Über uns"
        titleLines={["Zwei Perspektiven.", "Ein Ziel:"]}
        titleAccent="besseres Sponsoring."
        lead="MatchGate entsteht aus der Kombination von Technik, wirtschaftlichem Verständnis und dem Wunsch, Vereinssponsoring transparenter, zugänglicher und messbarer zu machen."
        primary={{ label: "Team kennenlernen", href: "#team", icon: ArrowDown }}
        secondary={{ label: "Kontakt aufnehmen", to: ROUTE_CONTACT }}
      >
        <WhyCard />
      </PageHero>

      {/* Warum es uns gibt */}
      <section
        id="warum"
        data-testid="about-why-section"
        className="relative py-24 lg:py-32"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[460px] rounded-full bg-brand-orange/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Warum es uns gibt"
                title="Sponsoring ist die "
                accent="unsichtbarste"
                tail=" Säule des Sports."
                className="max-w-md mb-0"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <Reveal>
              <p className="text-lg md:text-xl leading-relaxed text-white/70">
                In Deutschland hängt der Breitensport an Menschen, die neben
                Beruf und Familie Trikots organisieren, Hallenzeiten
                koordinieren und Sponsoren suchen. Der letzte Punkt kostet am
                meisten Kraft und liefert am wenigsten zurück - weil es keine
                Struktur dafür gibt.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-base md:text-lg leading-relaxed text-white/55">
                Auf der anderen Seite stehen Unternehmen, die regional etwas
                bewegen wollen und dafür ein Budget haben. Sie scheitern nicht
                am Willen, sondern daran, dass sie nicht wissen, welcher Verein
                zu ihnen passt - und dass sie am Jahresende nichts in der Hand
                halten außer einem Foto von der Bandenwerbung.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed text-white/55">
                MatchGate schließt genau diese Lücke. Wir bringen beide Seiten
                strukturiert zusammen und machen sichtbar, was aus der
                Partnerschaft entsteht. Nicht als Werbeversprechen, sondern als
                Zahl, die man nachprüfen kann.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10 mt-10">
                <div className="pointer-events-none absolute -bottom-20 -right-16 w-72 h-72 rounded-full bg-brand-blue/20 blur-[100px]" />
                <p className="relative font-display font-bold text-2xl md:text-3xl leading-snug text-white">
                  Unser Ziel ist banal und ziemlich groß zugleich: dass kein
                  Verein mehr ein gutes Projekt aufgibt, weil ihm die Zeit für
                  die Sponsorensuche fehlt.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Team */}
      <section
        id="team"
        data-testid="about-team-section"
        className="relative py-24 lg:py-32 border-y border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Das Team"
            title="Informatik trifft "
            accent="Wirtschaft"
            tail="."
            lead="Hinter MatchGate stehen zwei Gründer, die Technik und Wirtschaft zusammenbringen. Die Fotos folgen in Kürze."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-2">
            {TEAM.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.name} delay={i * 0.08}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-6 md:p-8 hover:border-white/20 transition-colors duration-500">
                    <div className="grid sm:grid-cols-9 gap-7 md:gap-8 items-center">
                      <div className="sm:col-span-4">
                        <PhotoPlaceholder initial={m.initial} tint={m.tint} />
                      </div>

                      <div className="sm:col-span-5">
                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                          <Icon
                            className={`w-5 h-5 ${
                              m.tint === "orange"
                                ? "text-brand-orange"
                                : "text-brand-blue"
                            }`}
                          />
                        </span>
                        <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/35 mb-3">
                          {m.role}
                        </span>
                        <h3 className="font-display font-bold text-2xl md:text-3xl text-white">
                          {m.name}
                        </h3>
                        <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-brand-orange hyphens-auto [overflow-wrap:anywhere]">
                          <GraduationCap className="w-4 h-4 shrink-0" />
                          {m.degree}
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-white/55">
                          {m.text}
                        </p>
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Unser Ansatz */}
      <section
        id="ansatz"
        data-testid="about-approach-section"
        className="relative py-24 lg:py-32"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute bottom-0 right-0 w-[560px] h-[420px] rounded-full bg-brand-blue/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Unser Ansatz"
            title="Wir bauen MatchGate mit "
            accent="praktischem Blick"
            tail="."
            className="max-w-3xl mb-14"
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {APPROACH.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={i * 0.07}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                      <Icon
                        className={`w-5 h-5 ${
                          i === 1 ? "text-brand-blue" : "text-brand-orange"
                        }`}
                      />
                    </span>
                    <h3 className="font-display font-bold text-2xl text-white mb-3">
                      {a.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/55">
                      {a.text}
                    </p>
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        title="Ihr wollt wissen, ob das zu euch "
        accent="passt"
        tail="?"
        lead="Egal ob Verein, Unternehmen oder Verband - erzählt uns von eurer Situation. Wir sagen ehrlich, ob MatchGate hilft."
        primary={{ label: "Kontakt aufnehmen", to: ROUTE_CONTACT }}
        secondary={{ label: "Demo ansehen", to: ROUTE_DEMO }}
      />
    </PageLayout>
  );
}
